import { useMemo, useState } from 'react';
import { sankey, sankeyLinkHorizontal, sankeyLeft, type SankeyGraph } from 'd3-sankey';
import {
  PAYLOAD_CATEGORY_EDGE_COLOR,
  collapseLowWeightEdges,
  type DataSharingGraph,
  type DataSharingEdge,
  type DataSharingDestinationNode,
  type DataSharingScriptNode,
} from '@/lib/data-sharing-graph';

interface DataSharingMapProps {
  graph: DataSharingGraph;
  onSelectDestination?: (node: DataSharingDestinationNode | null) => void;
  onSelectScript?: (node: DataSharingScriptNode | null) => void;
}

type SankeyNodeData =
  | (DataSharingScriptNode & { kind: 'script' })
  | (DataSharingDestinationNode & { kind: 'destination' });

interface SankeyLinkData {
  source: string | number | SankeyNodeData;
  target: string | number | SankeyNodeData;
  value: number;
  edge: DataSharingEdge;
}

const WIDTH = 960;
const HEIGHT = 540;
const PADDING = 24;

// Bipartite left-right layout computed by d3-sankey. Scripts live in
// the left column; destinations in the right. Edge colour encodes the
// dominant payload category; edge thickness encodes payload count.
export function DataSharingMap({
  graph,
  onSelectDestination,
  onSelectScript,
}: DataSharingMapProps) {
  const [hoveredEdge, setHoveredEdge] = useState<DataSharingEdge | null>(null);

  const compact = useMemo(() => collapseLowWeightEdges(graph, 200), [graph]);

  const layout = useMemo<SankeyGraph<SankeyNodeData, SankeyLinkData> | null>(() => {
    if (compact.scripts.length === 0 || compact.destinations.length === 0) return null;

    const nodes: SankeyNodeData[] = [
      ...compact.scripts.map<SankeyNodeData>((s) => ({ ...s, kind: 'script' })),
      ...compact.destinations.map<SankeyNodeData>((d) => ({ ...d, kind: 'destination' })),
    ];
    const links: SankeyLinkData[] = compact.edges.map((e) => ({
      source: e.sourceId,
      target: e.targetId,
      value: Math.max(1, e.weight),
      edge: e,
    }));

    const generator = sankey<SankeyNodeData, SankeyLinkData>()
      .nodeId((d) => d.id)
      .nodeAlign(sankeyLeft)
      .nodeWidth(14)
      .nodePadding(10)
      .extent([
        [PADDING, PADDING],
        [WIDTH - PADDING, HEIGHT - PADDING],
      ]);

    return generator({
      nodes: nodes.map((n) => ({ ...n })),
      links: links.map((l) => ({ ...l })),
    });
  }, [compact]);

  if (!layout) {
    return (
      <div className="rounded-md border bg-background p-6 text-sm text-muted-foreground" data-testid="data-sharing-empty">
        No payloads observed for the current filters — switch consent mode or toggle sensitive-only off.
      </div>
    );
  }

  const linkPath = sankeyLinkHorizontal<SankeyNodeData, SankeyLinkData>();

  return (
    <svg
      role="img"
      aria-label="Data sharing map"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-full max-w-full rounded-md border bg-white"
      data-testid="data-sharing-map"
    >
      <g fill="none">
        {layout.links.map((l, idx) => {
          const d = linkPath(l);
          if (!d) return null;
          const edge = (l as unknown as { edge: DataSharingEdge }).edge;
          const color = PAYLOAD_CATEGORY_EDGE_COLOR[edge.dominantCategory];
          const accepted = edge.consentModes.has('accept-all');
          const otherModes = Array.from(edge.consentModes).some((m) => m !== 'accept-all');
          const dashed = accepted && !otherModes;
          const thickness = Math.max(1, l.width ?? 1);
          return (
            <path
              key={idx}
              d={d}
              stroke={color}
              strokeOpacity={hoveredEdge === edge ? 0.85 : 0.5}
              strokeWidth={thickness}
              strokeDasharray={hoveredEdge === edge && dashed ? '6 4' : undefined}
              onMouseEnter={() => setHoveredEdge(edge)}
              onMouseLeave={() => setHoveredEdge(null)}
              data-testid={`data-sharing-edge-${edge.sourceId}-${edge.targetId}`}
              data-dominant-category={edge.dominantCategory}
            >
              <title>
                {`${edge.weight} requests · ${edge.dominantCategory} · modes: ${Array.from(edge.consentModes).join(', ')}`}
              </title>
            </path>
          );
        })}
      </g>
      <g>
        {layout.nodes.map((n) => {
          const node = n as unknown as SankeyNodeData & {
            x0: number;
            x1: number;
            y0: number;
            y1: number;
          };
          const label = node.kind === 'script' ? node.label : node.domain;
          const isScript = node.kind === 'script';
          return (
            <g
              key={node.id}
              transform={`translate(${node.x0},${node.y0})`}
              data-testid={`data-sharing-node-${node.id}`}
              onClick={() => {
                if (node.kind === 'destination') onSelectDestination?.(node);
                else onSelectScript?.(node);
              }}
              className="cursor-pointer"
            >
              <rect
                width={node.x1 - node.x0}
                height={Math.max(1, node.y1 - node.y0)}
                fill={isScript ? '#475569' : '#0f172a'}
              />
              <text
                x={isScript ? (node.x1 - node.x0) + 4 : -4}
                y={(node.y1 - node.y0) / 2}
                dy="0.35em"
                textAnchor={isScript ? 'start' : 'end'}
                fontSize={10}
                fill="#0f172a"
                className="pointer-events-none select-none"
              >
                {label.length > 40 ? `${label.slice(0, 39)}…` : label}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}
